import { Delete, Edit, Gavel, VisibilityOff } from "@mui/icons-material"
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
} from "@mui/material"
import { useRef, useState } from "react"
import { NexCheckbox } from "../../components/Fields/Checkbox"
import { NexTableCell } from "../../components/Table/NexTableCell"
import { NexHeaderCell } from "../../components/Table/NexTableHeader"
import { NexTableToolbarBase, ToolDivider } from "../../components/Table/NexTableToolbar"
import { Tool } from "../../components/Tool/Tool"
import { FlightlineEvent, flightLineEventId } from "../../models/FlightlineEvent"
import { NexHeaderCellPropsBase } from "../../models/Table"
import { FlightlineStatusOption, operatorLineStatusOptions } from "../../store/types/MissionsTypes"
import { unplannedLineLabel } from "../../utils/FlightlineEventUtils"
import { useCollectionEventEditorStore } from "../../zstore/CollectionEventEditorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { FlightlineTableRow } from "./FlightlineTableRow"

export const MissionEditFlightlineEventsTable = () => {
    const scrollRef = useRef<HTMLDivElement>(null)

    const [page, setPage] = useState<number>(0)
    const [rowsPerPage, setRowsPerPage] = useState<number>(20)
    const [filterIgnored, setFilterIgnored] = useState(false)

    // Local Storage Store
    const preferredHeightUnit = useLocalStorageStore((state) => state.preferredHeightUnit)
    const preferredDistanceUnit = useLocalStorageStore((state) => state.preferredDistanceUnit)

    // Flightline Event Store
    const flightlineEvents = useFlightlineEventStore((state) => state.flightlineEvents)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const updateEventStatuses = useFlightlineEventStore((state) => state.updateEventStatuses)
    const selectedEventIds = useFlightlineEventStore((state) => state.selectedEventIds)
    const updateSelectedEventIds = useFlightlineEventStore((state) => state.updateSelectedEventIds)

    // Collection Event Editor Store
    const setCollectionEvent = useCollectionEventEditorStore((state) => state.setValuesFromFlightline)

    let shownEvents = flightlineEvents
    if (filterIgnored) {
        shownEvents = shownEvents.filter((event) => event.flight_status !== "Ignored")
    }

    const handleIgnored = () => {
        setFilterIgnored((prev) => !prev)
        if (!filterIgnored) {
            const filterOutIgnoredRowsFromSelected = selectedEventIds.filter((selectedId) => {
                const row = flightlineEvents.find((event) => event.fe_event_id === selectedId)
                return row?.flight_status !== "Ignored"
            })
            updateSelectedEventIds(filterOutIgnoredRowsFromSelected)
        }
    }

    const setEditCollectionEvent = (id: number) => {
        const existingLine = flightlineEvents.find((row) => row[flightLineEventId] === id)
        if (existingLine) {
            setCollectionEvent(existingLine)
        }
    }

    const resetTableScroll = () => {
        if (scrollRef?.current) {
            scrollRef.current.scrollTop = 0
        }
    }

    const handleChangePage = (newPage: number) => {
        setPage(newPage)
        resetTableScroll()
    }

    const handleChangeRowsPerPage = (value: number) => {
        setRowsPerPage(value)
        setPage(0)
        resetTableScroll()
    }

    const handleDeleteSelected = () => {
        const eventsWithoutSelected = flightlineEvents.filter((event) => !selectedEventIds.includes(event.fe_event_id))
        updateFlightlineEvents(eventsWithoutSelected)
        updateSelectedEventIds([])
    }

    const handleSelectAll = (prevNumSelected: number) => {
        let selected: number[] = []
        if (prevNumSelected === 0) {
            // select all
            selected = shownEvents.map((event) => event.fe_event_id)
        }
        updateSelectedEventIds(selected)
    }

    return (
        <Paper>
            <NexTableToolbarBase label="Flightline Events" selectedRowIds={selectedEventIds}>
                <Tool
                    value={filterIgnored}
                    tooltip="Hide Ignored Lines"
                    Icon={VisibilityOff}
                    onChange={handleIgnored}
                />
                <Tool
                    tooltip="Change Status of Selected"
                    Icon={Gavel}
                    onChange={(option) => {
                        if (option) {
                            updateEventStatuses(option as FlightlineStatusOption, selectedEventIds)
                        }
                    }}
                    options={operatorLineStatusOptions}
                    disabled={selectedEventIds.length === 0}
                />
                <ToolDivider />
                <Tool
                    tooltip={"Edit Line"}
                    Icon={Edit}
                    onChange={() => setEditCollectionEvent(selectedEventIds[0])}
                    disabled={selectedEventIds.length === 0 || selectedEventIds.length > 1}
                />
                <Tool
                    tooltip="Delete"
                    disabled={selectedEventIds.length === 0}
                    Icon={Delete}
                    value={selectedEventIds.length !== 0}
                    onChange={handleDeleteSelected}
                />
            </NexTableToolbarBase>
            <TableContainer ref={scrollRef} sx={{ maxHeight: 400, minHeight: 200, overflowY: "auto" }}>
                <Table stickyHeader={true} size="small">
                    <TableHead>
                        <TableRow>
                            <NexTableCell stickyHeader={true}>
                                <NexTableCell>
                                    <NexCheckbox
                                        onClick={() => handleSelectAll(selectedEventIds.length)}
                                        checked={selectedEventIds.length > 0}
                                    />
                                </NexTableCell>
                                <NexFlightlineHeaderCell label="Flight Plan - Name" columnId="fe_planned_line_ids" />
                            </NexTableCell>
                            <NexFlightlineHeaderCell label="Original Name" columnId="line_original_name" />
                            <NexFlightlineHeaderCell label="Sensor Label" columnId="coll_event_name" />
                            <NexTableCell>Status</NexTableCell>
                            <NexTableCell>Notes</NexTableCell>
                            <NexTableCell>Acquired Nodes</NexTableCell>
                            <NexTableCell>Sensor Details</NexTableCell>
                            <NexFlightlineHeaderCell label="Start" columnId="flight_line_start_time" />
                            <NexFlightlineHeaderCell label="End" columnId="flight_line_stop_time" />
                            <NexTableCell>Dist. ({preferredDistanceUnit})</NexTableCell>
                            <NexTableCell>Dur. (s)</NexTableCell>
                            <NexTableCell>AGL. ({preferredHeightUnit})</NexTableCell>
                            <NexTableCell sx={{ p: 0 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shownEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((event) => {
                            return <FlightlineTableRow key={event.fe_event_id} event={event} />
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TableContainer>
                <Table>
                    <TableFooter>
                        <TableRow>
                            <TableCell>Flight Plan: ({unplannedLineLabel}) Unplanned Line</TableCell>
                            <TablePagination
                                sx={{ overflow: "initial" }}
                                rowsPerPageOptions={[20, 50, 100]}
                                count={flightlineEvents.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, page) => handleChangePage(page)}
                                onRowsPerPageChange={(e) => handleChangeRowsPerPage(+e.target.value)}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Paper>
    )
}

interface NexFlightlineHeaderCellProps extends NexHeaderCellPropsBase {
    columnId?: keyof FlightlineEvent
}

const NexFlightlineHeaderCell = (props: NexFlightlineHeaderCellProps) => {
    const sortOrder = useFlightlineEventStore((state) => state.sortOrder)
    const updateSortOrder = useFlightlineEventStore((state) => state.updateSortOrder)

    return (
        <NexTableCell>
            <NexHeaderCell setSortOrder={updateSortOrder} sortOrder={sortOrder} {...props} />
        </NexTableCell>
    )
}
